import { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabase'
import { ecrire, lire } from './erreurs'
import { CAPACITE_CAMION, camionsAuto } from './carnetUtils'
import {
  t, Stagger, Panel, PanelHead, PanelTitle, PageHead, PageTitle, PageSub,
  StatGrid, Tile, ChipBar, Chip, Btn, Input, Textarea, Label, Field, Row,
  Cluster, Pill, Loading, Empty, Select, useUI,
} from './ui'

const VIDE = { client_id: '', nom: '', email: '', telephone: '', canton: '', produit: '', tonnage: '', notes: '' }

const JOUR_MS = 86400000
const anciennete = (iso) => {
  const j = Math.floor((Date.now() - new Date(iso)) / JOUR_MS)
  if (j <= 0) return "aujourd'hui"
  if (j === 1) return 'depuis hier'
  if (j < 31) return `depuis ${j} jours`
  const m = Math.floor(j / 30)
  return `depuis ${m} mois`
}

/* Un groupe par marchandise : c'est la question qu'on se pose en
   trouvant du stock — « j'ai 60 T de foin, pour qui ? » */
const Groupe = styled.div`
  margin-bottom: 20px;
`
const GroupeTete = styled.div`
  display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap;
  padding: 0 2px 9px;
  border-bottom: 2px solid ${t.line};
  margin-bottom: 10px;
`
const GroupeNom = styled.div`
  font-family: ${t.serif}; font-size: 1.04rem; font-weight: 650;
  letter-spacing: -0.03em; color: ${t.text};
`
const GroupeTonnage = styled.div`
  margin-left: auto;
  font-family: ${t.font}; font-size: 0.82rem; font-weight: 600;
  color: ${t.gold}; font-variant-numeric: tabular-nums;
`
/* Le rang n'est pas décoratif : il dit qui est servi en premier. */
const Rang = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 21px; height: 21px; border-radius: 50%; flex: none;
  background: ${t.surfaceSunk}; color: ${t.textSoft};
  font-family: ${t.font}; font-size: 0.68rem; font-weight: 650;
  font-variant-numeric: tabular-nums;
`
const Fiche = styled.div`
  display: grid;
  grid-template-columns: 21px minmax(0, 1.5fr) minmax(0, 1fr) auto;
  gap: 12px; align-items: center;
  padding: 10px 13px; margin-bottom: 7px;
  background: ${t.surface};
  border: 1px solid ${t.line}; border-radius: ${t.rSm};
  transition: border-color 0.14s;
  opacity: ${(p) => (p.$fini ? 0.6 : 1)};
  &:hover { border-color: #DDDFDA; }

  @media (max-width: 760px) {
    grid-template-columns: 21px minmax(0, 1fr);
    row-gap: 7px;
  }
`
const Nom = styled.div`
  font-family: ${t.font}; font-size: 0.87rem; font-weight: 600; color: ${t.text};
  span { display: block; font-size: 0.74rem; font-weight: 500; color: ${t.textFaint}; margin-top: 2px; }
`
const Chiffre = styled.div`
  font-family: ${t.font}; font-size: 0.82rem; color: ${t.textSoft};
  font-variant-numeric: tabular-nums;
  b { color: ${t.text}; font-weight: 640; }
`
const Notes = styled.div`
  grid-column: 2 / -1;
  font-family: ${t.font}; font-size: 0.76rem; color: ${t.textSoft};
  padding-top: 2px;
  @media (max-width: 760px) { grid-column: 2 / -1; }
`

export default function ListeAttente() {
  const navigate = useNavigate()
  const { toast, confirm } = useUI()
  const [liste, setListe] = useState([])
  const [produits, setProduits] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtre, setFiltre] = useState('attente')
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => { charger() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const charger = async () => {
    setLoading(true)
    const data = await lire(
      supabase.from('liste_attente').select('*').order('created_at'),
      toast, 'chargement de la liste d\'attente',
    )
    if (data) setListe(data)
    const p = await lire(
      supabase.from('products').select('name').order('name'),
      toast, 'chargement des produits',
    )
    if (p) setProduits(p.map((x) => x.name))
    const c = await lire(
      supabase.from('clients').select('id, name, email, phone, canton').order('name'),
      toast, 'chargement des clients',
    )
    if (c) setClients(c)
    setLoading(false)
  }

  const visibles = useMemo(
    () => liste.filter((l) => (filtre === 'tout' ? true : l.statut === filtre)),
    [liste, filtre],
  )

  /* Regroupement par marchandise, chaque file dans son ordre d'arrivée. */
  const groupes = useMemo(() => {
    const m = {}
    visibles.forEach((l) => { (m[l.produit] = m[l.produit] || []).push(l) })
    return Object.entries(m).sort((a, b) => b[1].length - a[1].length)
  }, [visibles])

  const enAttente = liste.filter((l) => l.statut === 'attente')
  const tonnageAttendu = enAttente.reduce((s, l) => s + (parseFloat(l.tonnage) || 0), 0)
  const plusAncien = enAttente[0]

  /* Un client connu pré-remplit la fiche sans la verrouiller : ses
     coordonnées peuvent avoir changé, et l'inscription est un relevé
     du moment. */
  const choisirClient = (id) => {
    const c = clients.find((x) => x.id === id)
    if (!c) return setForm((p) => ({ ...p, client_id: '' }))
    setForm((p) => ({
      ...p, client_id: c.id, nom: c.name || '',
      email: c.email || '', telephone: c.phone || '', canton: c.canton || '',
    }))
  }

  /* Reprend une inscription dans le formulaire. Sans `id`, l'enregistrement
     en créera une nouvelle : c'est ce qui distingue la copie de l'édition. */
  const formDepuis = (l, { copie = false } = {}) => ({
    id: copie ? undefined : l.id,
    client_id: l.client_id || '',
    nom: l.nom,
    email: l.email || '',
    telephone: l.telephone || '',
    canton: l.canton || '',
    produit: l.produit,
    tonnage: l.tonnage ?? '',
    notes: l.notes || '',
  })

  const enregistrer = async () => {
    if (!form.nom.trim() || !form.produit.trim()) {
      return toast('Le nom et la marchandise sont requis', 'error')
    }
    setSaving(true)
    const payload = {
      nom: form.nom.trim(), email: form.email || null, telephone: form.telephone || null,
      canton: form.canton || null, client_id: form.client_id || null,
      produit: form.produit.trim(),
      tonnage: form.tonnage === '' ? null : parseFloat(form.tonnage),
      notes: form.notes || null, updated_at: new Date().toISOString(),
    }
    const ok = await ecrire(
      form.id
        ? supabase.from('liste_attente').update(payload).eq('id', form.id)
        : supabase.from('liste_attente').insert([payload]),
      toast, 'enregistrement de l\'inscription',
    )
    setSaving(false)
    if (!ok) return
    toast(form.id ? 'Inscription mise à jour' : 'Client inscrit sur la liste')
    setForm(null)
    charger()
  }

  const changerStatut = async (l, statut) => {
    const ok = await ecrire(
      supabase.from('liste_attente').update({
        statut,
        servi_le: statut === 'servi' ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      }).eq('id', l.id),
      toast, 'mise à jour de l\'inscription',
    )
    if (!ok) return
    toast(statut === 'servi' ? `${l.nom} sorti de la file` : `${l.nom} annulé`)
    charger()
  }

  const supprimer = async (l) => {
    const ok = await confirm({
      icon: '🗑️', tone: 'red',
      title: 'Retirer cette inscription ?',
      body: <><strong>{l.nom}</strong> disparaîtra de la liste d'attente. Son devis
        et sa fiche client ne sont pas touchés.</>,
      confirmLabel: 'Retirer',
    })
    if (!ok) return
    await supabase.from('liste_attente').delete().eq('id', l.id)
    toast('Inscription retirée')
    charger()
  }

  if (loading) return <Loading rows={4} h="80px" />

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>Liste d'attente</PageTitle>
          <PageSub>
            {enAttente.length} client(s) en attente · {tonnageAttendu.toFixed(1)} T à trouver
            {tonnageAttendu > 0 ? `, soit ${camionsAuto(tonnageAttendu)} camion(s) remorque` : ''}
          </PageSub>
        </div>
        <Btn $variant="gold" onClick={() => setForm({ ...VIDE })}>+ Inscrire un client</Btn>
      </PageHead>

      <StatGrid style={{ marginBottom: 18 }}>
        <Tile i={0} label="En attente" value={`${enAttente.length}`}
          hint={`${groupes.length} marchandise(s)`} tone="amber" />
        <Tile i={1} label="Tonnage à trouver" value={`${tonnageAttendu.toFixed(1)} T`}
          hint={tonnageAttendu > 0
            ? `${camionsAuto(tonnageAttendu)} camion(s) remorque · ${CAPACITE_CAMION} T l'un`
            : 'pour servir toute la file'}
          tone="gold" />
        <Tile i={2} label="Plus ancienne demande"
          value={plusAncien ? anciennete(plusAncien.created_at).replace('depuis ', '') : '—'}
          hint={plusAncien ? plusAncien.nom : 'file vide'} tone="blue" />
        <Tile i={3} label="Servis" value={`${liste.filter((l) => l.statut === 'servi').length}`}
          hint="sortis de la file" tone="green" />
      </StatGrid>

      <ChipBar style={{ marginBottom: 16 }}>
        <Chip $on={filtre === 'attente'} onClick={() => setFiltre('attente')}>⏳ En attente</Chip>
        <Chip $on={filtre === 'servi'} onClick={() => setFiltre('servi')}>✅ Servis</Chip>
        <Chip $on={filtre === 'annule'} onClick={() => setFiltre('annule')}>🚫 Annulés</Chip>
        <Chip $on={filtre === 'tout'} onClick={() => setFiltre('tout')}>Tout</Chip>
      </ChipBar>

      {form && (
        <Stagger style={{ marginBottom: 18 }}>
          <Panel $accent>
            <PanelHead>
              <PanelTitle>{form.id ? 'Modifier l\'inscription' : 'Inscrire un client'}</PanelTitle>
              <Btn $variant="quiet" $size="sm" onClick={() => setForm(null)}>✕ Fermer</Btn>
            </PanelHead>

            <Field>
              <Label>Client déjà connu</Label>
              <Select value={form.client_id} onChange={(e) => choisirClient(e.target.value)}>
                <option value="">— Saisie manuelle —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.canton ? ` · ${c.canton}` : ''}
                  </option>
                ))}
              </Select>
            </Field>

            <Row $cols="1fr 1fr">
              <Field>
                <Label>Client *</Label>
                <Input value={form.nom} onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                  placeholder="Nom ou exploitation" />
              </Field>
              <Field>
                <Label>Marchandise attendue *</Label>
                <Input list="produits-connus" value={form.produit}
                  onChange={(e) => setForm((p) => ({ ...p, produit: e.target.value }))}
                  placeholder="Foin de Prairie naturelle, Luzerne…" />
                <datalist id="produits-connus">
                  {produits.map((p) => <option key={p} value={p} />)}
                </datalist>
              </Field>
            </Row>

            <Row $cols="120px 1fr 1fr 140px">
              <Field>
                <Label>Tonnage</Label>
                <Input type="number" step="0.1" value={form.tonnage}
                  onChange={(e) => setForm((p) => ({ ...p, tonnage: e.target.value }))} placeholder="24" />
              </Field>
              <Field>
                <Label>Email</Label>
                <Input type="email" value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
              </Field>
              <Field>
                <Label>Téléphone</Label>
                <Input value={form.telephone}
                  onChange={(e) => setForm((p) => ({ ...p, telephone: e.target.value }))} />
              </Field>
              <Field>
                <Label>Canton</Label>
                <Input value={form.canton}
                  onChange={(e) => setForm((p) => ({ ...p, canton: e.target.value }))} placeholder="Fribourg" />
              </Field>
            </Row>

            <Field>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                style={{ minHeight: 68 }} placeholder="Qualité souhaitée, période, contraintes…" />
            </Field>

            <Cluster $end $gap="9px">
              <Btn $variant="gold" onClick={enregistrer} disabled={saving}>
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Btn>
            </Cluster>
          </Panel>
        </Stagger>
      )}

      {groupes.length === 0 ? (
        <Empty icon="⏳" title="Personne en attente">
          Inscrivez un client depuis sa fiche devis, ou directement ici, lorsque la
          marchandise qu'il demande vient à manquer.
        </Empty>
      ) : (
        groupes.map(([produit, files], gi) => {
          const tonnage = files.reduce((s, l) => s + (parseFloat(l.tonnage) || 0), 0)
          return (
            <Stagger key={produit} $i={gi}>
              <Groupe>
                <GroupeTete>
                  <GroupeNom>{produit}</GroupeNom>
                  <Pill $tone="neutral">{files.length}</Pill>
                  {tonnage > 0 && (
                    <GroupeTonnage>
                      {tonnage.toFixed(1)} T à trouver · {camionsAuto(tonnage)} camion(s)
                    </GroupeTonnage>
                  )}
                </GroupeTete>

                {files.map((l, i) => (
                  <Fiche key={l.id} $fini={l.statut !== 'attente'}>
                    <Rang>{i + 1}</Rang>

                    <Nom>
                      {l.nom}
                      <span>
                        {[l.canton, l.telephone, l.email].filter(Boolean).join(' · ') || 'aucun contact'}
                      </span>
                    </Nom>

                    <Chiffre>
                      {l.tonnage ? <b>{l.tonnage} T</b> : <i>tonnage libre</i>}
                      <div style={{ fontSize: '0.74rem', color: t.textFaint }}>
                        {anciennete(l.created_at)}
                      </div>
                    </Chiffre>

                    <Cluster $gap="6px">
                      {l.statut === 'attente' ? (
                        <>
                          <Btn $variant="gold" $size="sm" onClick={() => changerStatut(l, 'servi')}>
                            ✅ Servir
                          </Btn>
                          <Btn $variant="ghost" $size="sm" onClick={() => changerStatut(l, 'annule')}>
                            Annuler
                          </Btn>
                        </>
                      ) : (
                        <Pill $tone={l.statut === 'servi' ? 'green' : 'neutral'}>
                          {l.statut === 'servi' ? 'servi' : 'annulé'}
                        </Pill>
                      )}
                      {l.quote_id && (
                        <Btn $variant="quiet" $size="sm"
                          onClick={() => navigate(`/admin/devis/${l.quote_id}`)}>
                          Devis →
                        </Btn>
                      )}
                      <Btn $variant="quiet" $size="sm" title="Modifier"
                        onClick={() => setForm(formDepuis(l))}>✎</Btn>
                      <Btn $variant="quiet" $size="sm" title="Dupliquer — même client, autre marchandise ou autre tonnage"
                        onClick={() => setForm(formDepuis(l, { copie: true }))}>⧉</Btn>
                      <Btn $variant="quiet" $size="sm" onClick={() => supprimer(l)}>🗑</Btn>
                    </Cluster>

                    {l.notes && <Notes>{l.notes}</Notes>}
                  </Fiche>
                ))}
              </Groupe>
            </Stagger>
          )
        })
      )}
    </>
  )
}
